import type { DealItemsActionType } from '@/lib/deal-events.types';
import {
  DealItemWithVersions,
  ValidationError,
} from '@/lib/graphql/generated/graphql';
import type { ErrorLike } from '@apollo/client';
import { atom } from 'jotai';

export const productsMapState = atom<Map<string, DealItemWithVersions>>(
  new Map()
);

export const dealItemsLoadingState = atom<{
  initialLoading: boolean;
  error: ErrorLike | undefined;
}>({ initialLoading: true, error: undefined });

export const dealItemsStreamingState = atom<boolean>(false);

export const dealItemsRefetchState = atom<{
  fn: () => Promise<boolean>;
} | null>(null);

/**
 * Enregistré par `useDealItems`, appelé depuis `page-layout` quand le canal SSE
 * signale un changement d'items.
 *
 * Va plus loin que `dealItemsRefetchState` : en plus de relire les items, ce
 * callback reproduit les effets de bord que les handlers de la subscription
 * WebSocket portaient (résolution des toasts, invalidation de la pile undo,
 * réveil du fallback catégorie Shopify), en s'appuyant sur l'`action` du signal.
 */
export const dealItemsSignalState = atom<{
  fn: (action?: DealItemsActionType) => Promise<void>;
} | null>(null);

export const localValidationErrorsAtom = atom<Map<
  string,
  ValidationError[]
> | null>(null);

export const backendValidatedProductIdsAtom = atom<Set<string>>(
  new Set<string>()
);

export const productsState = atom(
  (get) => Array.from(get(productsMapState).values()),
  (
    _get,
    set,
    update:
      | DealItemWithVersions[]
      | ((prev: DealItemWithVersions[]) => DealItemWithVersions[])
  ) => {
    set(productsMapState, (prevMap) => {
      const newArray =
        typeof update === 'function'
          ? update(Array.from(prevMap.values()))
          : update;
      const nextMap = new Map<string, DealItemWithVersions>();
      for (const p of newArray) nextMap.set(p.id, p);
      return nextMap;
    });
  }
);
